/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import { Logger } from "../../../clients/logger";
import { BootTaskCallbackInterface } from "../types";
import { RouterClient } from "../../../clients/routerClient";
import { ConfigClient } from "../../../clients/configClient";
import { WorkspaceClient } from "../../../clients/workspaceClient";
import { DialogManagerClient } from "../../../clients/dialogManagerClient";
import { calculateTimeout } from "./checkForScheduledShutdownTask";

let SCHEDULED_RESTART_TIMEOUT = 60000;

/**
 * Boot task that sets up the "Finsemble" wildcard pubsub for use throughout Finsemble.
 * @private
 */
export class CheckForScheduledRestartTask {
	public start(doneCallback: BootTaskCallbackInterface) {
		Logger.system.debug("checkForScheduledRestartTask start");
		this.checkForScheduledRestart();

		doneCallback("checkForScheduledRestartTask", "bootTasks", "completed");
	}

	/**
	 * Checks config for a scheduledRestart property. If it isn't falsy, it will be an object of format:
	 * {
	 * hour: 16,
	 * minute:30,
	 * dialogTimeout:10000
	 * }
	 * If it exists, we set a timeout that will fire at that time.
	 */
	private checkForScheduledRestart() {
		let scheduledRestart: ReturnType<typeof setTimeout> | null = null;
		// Handles
		const scheduleRestart = (err, cfg) => {
			Logger.system.log("checkForScheduledRestart scheduleRestart.", err, cfg);

			// If we already had a restart scheduled, clear it. There's a new sheriff in town.
			if (scheduledRestart) {
				clearTimeout(scheduledRestart);
			}
			// They may just disable the restart. If so, don't do anything.
			if (!cfg.value) return;
			const diff = calculateTimeout(cfg.value);

			Logger.system.log("checkForScheduledRestart scheduleRestart.", err, cfg);

			scheduledRestart = setTimeout(async () => {
				Logger.system.log("Scheduling restart");

				let { err: error, data: discardUnsavedChanges = false } = await ConfigClient.getValue(
					"finsemble.scheduledCloseDiscardUnsavedChanges"
				);
				if (error) {
					Logger.system.warn(
						"checkForScheduledRestart unable to get config value for finsemble.scheduledCloseDiscardUnsavedChanges - will save workspace",
						err
					);
				}

				DialogManagerClient.open(
					"yesNo",
					{
						monitor: "primary",
						title: "Automatic Restart",
						question: `The application will restart in one minute. ${
							discardUnsavedChanges
								? "Unsaved changes in your workspace will be discarded"
								: "Your workspace will be saved"
						}.`,
						showTimer: true,
						timerDuration: SCHEDULED_RESTART_TIMEOUT,
						showNegativeButton: false,
						affirmativeResponseLabel: "Restart Now",
					},
					async (dialogManagerErr, response) => {
						if (response.choice === "cancel") {
							// This code will set the scheduled restart to what it's currently set in.
							// When the scheduled restart is set to a time that's in the past, the application will schedule the restart for the next day.
							ConfigClient.getValue({ field: "finsemble.scheduledRestart" }, (configError, val) => {
								ConfigClient.setPreference({
									field: "finsemble.scheduledRestart",
									value: val,
								});
							});
						} else {
							if (discardUnsavedChanges) {
								Logger.system.info("checkForScheduledRestart discarding workspace unsaved changes", err);
							} else {
								Logger.system.info("checkForScheduledRestart saving current unsaved workspace changes", err);
								await WorkspaceClient.save();
							}

							// If we get here, they clicked "Restart Now", so we obey the user.
							RouterClient.transmit("Application.restart", {
								scheduledCloseDiscardUnsavedChanges: discardUnsavedChanges,
							});
						}
					}
				);
			}, diff);
		};

		// See if the user has set a pref for a scheduled restart. Or the dev. The dev could set it too.
		ConfigClient.getValue({ field: "finsemble.scheduledRestart" }, (err, config) => {
			// Allow the timeout for the restart dialog to be driven by config. See checkForScheduledRestart comments for format.

			// If the dialogTimeout property exists and is a number, override our default.
			if (config && !isNaN(config.dialogTimeout)) {
				SCHEDULED_RESTART_TIMEOUT = config.dialogTimeout;
			}

			Logger.system.log("scheduledRestart initial config", config);

			// create an object for the 2nd arg so that the scheduleRestart function doesn't have to change.
			scheduleRestart(err, { value: config });
		});

		// If the user changes it via the preferences API, we catch the change here, log it out, and schedule the restart.
		ConfigClient.addListener({ field: "finsemble.scheduledRestart" }, (err, config) => {
			Logger.system.log("scheduledRestart new config", config);
			if (config.value) {
				Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED RESTART TIME CHANGED. NEW TIME:", config.value);
			} else {
				Logger.system.log("APPLICATION LIFECYCLE:SCHEDULED RESTART DISABLED.");
			}

			scheduleRestart(err, config);
		});
	}
}
